import { useCallback, useMemo, useState } from "react"
import _ from "lodash"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Slide,
  Zoom,
  Button,
  useTheme,
  Paper,
  Avatar,
  CardMedia,
} from "@mui/material"
import clsx from "clsx"
import { Trans } from "react-i18next"

import { getIcon } from "utils/icon"
import { isValidURL } from "utils/url"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useStepByStep } from "hooks/direction/useStepByStep"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"
import { useStepTracking } from "hooks/analytics"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"

import {
  PrevStepIcon,
  NextStepIcon,
  MarkerIcon,
  OriginMarkerIcon,
  PlayArrowIcon,
  RefreshArrowIcon,
  PauseIcon,
} from "components/icons"
import { StepProgressBar } from "components/Progress/StepProgressBar"
import { OrdinalSelector } from "components/OrdinalSelector"
import { ExitIcon } from "components/icons"
import { VerticalDirectionImageDialog } from "components/Dialog/VerticalDirectionImageDialog"
import { CoolDownProgress } from "extensions/iconsiam/components/CoolDownProgress"

export const StepByStepKioskVPage = () => {
  const navigate = useNavigate()
  const [animateScroll, setAnimateScroll] = useState(false)
  const { dataLoaded, isMultiOrdinalVenue, isMultiVenueProject } = useVenue()

  const {
    isReady,
    steps,
    currentStepNumber,
    currentStep,
    currentOriginStepLevelName,
    currentDestStepLevelName,
    currentLevel,
    currentOrdinal,
    originName,
    route,
    destinationName,
    originLogo,
    originVenueLogo,
    destinationLogo,
    destinationVenueLogo,
    hasNextStep,
    hasPrevStep,
    currentVerticalStepMethod,
    directionOverviewPath,
    stepAutoplayConfig,
    isPlaying,
    destinationCategoryIconPath,
    originCategoryIconPath,
    goToStepByIndex,
    goToPrevStep,
    goToNextStep,
    restartAutoPlay,
    resumeAutoPlay,
    pauseAutoPlay,
  } = useStepByStep()

  useStepByStepMap(route, currentStepNumber, {
    offset: { bottom: 411, top: 0, left: 0, right: 0 },
    enabled: dataLoaded && steps.length > 0,
  })

  const { t } = useTranslation()

  useStepTracking({
    currentStep: currentStepNumber,
    steps,
    enabled: isReady,
  })

  const theme = useTheme()

  const {
    container,
    primaryText,
    title,
    locationText,
    originIcon,
    destinationIcon,
    navButtonWrapper,
    headerWrapper,
    contentWrapper,
    navButton,
    iconNavButton,
    ordinalSelectorWrapper,
    wrapper,
    highlightedText,
    stepTextContainer,
    exitButtonWrapper,
    exitButton,
    progressBar,
    stepProgressBar,
    originIconContainer,
    destinationIconContainer,
    originAvatar,
    actionButtonWrapper,
    stepProgressBarContainer,
    destinationAvatar,
    venueLogo,
    venueLogoContainer,
    cooldownProgress,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const exitIcon = useMemo(
    () => getIcon(theme?.assets?.exitIcon) || <ExitIcon />,
    [theme]
  )

  const originIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      ),
    [theme, originIcon]
  )

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <MarkerIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  const prevStepIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <PrevStepIcon />,
    [theme]
  )

  const nextStepIcon = useMemo(
    () => getIcon(theme?.assets?.nextIcon) || <NextStepIcon />,
    [theme]
  )

  const originCategoryIcon = useMemo(
    () => getIcon(originCategoryIconPath),
    [originCategoryIconPath]
  )

  const destinationCategoryIcon = useMemo(
    () => getIcon(destinationCategoryIconPath),
    [destinationCategoryIconPath]
  )

  const handleEnteredSlide = useCallback(() => {
    setAnimateScroll(true)
  }, [])

  const handleClickExit = useCallback(() => {
    navigate(directionOverviewPath, {
      replace: true,
    })
  }, [navigate, directionOverviewPath])

  const verticalDirectionImageUrl = useMemo(() => {
    const src = _.get(
      theme?.assets?.verticalStep?.[currentVerticalStepMethod],
      "url",
      ""
    )
    return isValidURL(src) ? src : sanitizePublicPath(src)
  }, [currentVerticalStepMethod, theme?.assets?.verticalStep])

  const showVerticalStepDialog = !!verticalDirectionImageUrl

  const isRtl = theme.isRtl

  return (
    <>
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={currentOriginStepLevelName}
        destinationLevelName={currentDestStepLevelName}
        open={showVerticalStepDialog}
      />
      <Slide
        direction="up"
        in={dataLoaded && steps.length > 0}
        onEntered={handleEnteredSlide}
        mountOnEnter
        unmountOnExit
      >
        <Box sx={wrapper}>
          {/* only show ordinalSelector when there is no VerticalDirectionImageDialog showing */}
          {isMultiOrdinalVenue && !showVerticalStepDialog && (
            <Box sx={ordinalSelectorWrapper}>
              <OrdinalSelector
                levels={currentLevel}
                selectedOrdinal={currentOrdinal}
              />
            </Box>
          )}
          <Box sx={actionButtonWrapper}>
            <Box sx={exitButtonWrapper}>
              <Button
                onClick={handleClickExit}
                startIcon={exitIcon}
                sx={exitButton}
                variant="contained"
              >
                {t("Exit")}
              </Button>
            </Box>
          </Box>
          <Paper sx={container}>
            <Stack
              width="100%"
              spacing={0}
              direction="column"
              height="100%"
              justifyContent="space-between"
            >
              <Box sx={contentWrapper}>
                <Box sx={headerWrapper}>
                  <Stack alignItems="center" spacing={1}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Zoom in={hasPrevStep}>
                        <IconButton
                          color="inherit"
                          sx={iconNavButton}
                          onClick={goToPrevStep}
                        >
                          <PrevStepIcon fontSize="inherit" />
                        </IconButton>
                      </Zoom>

                      <Typography
                        component={"h1"}
                        textAlign="center"
                        sx={title}
                      >
                        {t("Process step", {
                          currentStep: currentStepNumber,
                          totalSteps: steps.length,
                        })}
                      </Typography>

                      <Zoom in={hasNextStep}>
                        <IconButton
                          color="inherit"
                          sx={iconNavButton}
                          onClick={goToNextStep}
                        >
                          <NextStepIcon fontSize="inherit" />
                        </IconButton>
                      </Zoom>
                    </Stack>
                    <Box sx={stepTextContainer}>
                      <Typography
                        variant="subTitle"
                        component="p"
                        sx={primaryText}
                      >
                        <Trans
                          components={{
                            category: highlightedStepText,
                            destination: highlightedStepText,
                            landmark: highlightedStepText,
                            intermediary: highlightedStepText,
                            toward: highlightedStepText,
                            turn: highlightedStepText,
                          }}
                        >
                          {t(currentStep?.description.text, {
                            category: t(
                              currentStep?.description.options?.category
                            ),
                            destination: t(
                              currentStep?.description.options?.destination
                            ),
                            landmark: t(
                              currentStep?.description.options?.landmark
                            ),
                            intermediary: t(
                              currentStep?.description.options?.intermediary
                            ),
                            toward: t(currentStep?.description.options?.toward),
                            turn: t(currentStep?.description.options?.turn),
                          })}
                        </Trans>
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                {/* kiosk-v progress bar */}
                <Stack
                  width="100%"
                  height="100%"
                  direction="row"
                  alignItems={"flex-end"}
                  sx={progressBar}
                >
                  <Box sx={originIconContainer}>
                    <Avatar sx={originAvatar} src={originLogo}>
                      {originCategoryIcon ? originCategoryIcon : originIconComp}
                    </Avatar>
                    {isMultiVenueProject && originVenueLogo && (
                      <Box sx={venueLogoContainer}>
                        <CardMedia image={originVenueLogo} sx={venueLogo} />
                      </Box>
                    )}
                    <Box sx={{ height: "40px" }}>
                      <Typography
                        textAlign="start"
                        variant="caption"
                        sx={locationText}
                      >
                        {t(originName)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={stepProgressBarContainer}>
                    <StepProgressBar
                      steps={steps}
                      currentStep={currentStepNumber}
                      sx={stepProgressBar}
                      handleClickStep={goToStepByIndex}
                      enableAnimateScroll={animateScroll}
                    />
                  </Box>
                  <Box sx={destinationIconContainer}>
                    <Avatar sx={destinationAvatar} src={destinationLogo}>
                      {destinationCategoryIcon
                        ? destinationCategoryIcon
                        : destinationIconComp}
                    </Avatar>
                    {isMultiVenueProject && destinationVenueLogo && (
                      <Box sx={venueLogoContainer}>
                        <CardMedia
                          image={destinationVenueLogo}
                          sx={venueLogo}
                        />
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" sx={locationText}>
                        {t(destinationName)}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
                {/* end of kiosk-v progress bar */}
              </Box>
              <Stack sx={navButtonWrapper}>
                <Button
                  id="prev-step-button"
                  data-testid="prev-step-button"
                  variant="outlined"
                  className={isRtl ? "is-rtl" : ""}
                  startIcon={prevStepIcon}
                  disabled={!hasPrevStep}
                  sx={navButton}
                  onClick={goToPrevStep}
                >
                  {t("Previous Step")}
                </Button>
                {isPlaying ? (
                  <Button
                    variant="outlined"
                    sx={navButton}
                    startIcon={<PauseIcon />}
                    onClick={pauseAutoPlay}
                    disabled={!isPlaying}
                  >
                    {t("Stop")}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    sx={navButton}
                    startIcon={
                      hasNextStep ? <PlayArrowIcon /> : <RefreshArrowIcon />
                    }
                    onClick={hasNextStep ? resumeAutoPlay : restartAutoPlay}
                  >
                    {hasNextStep ? t("Play") : t("Play Again")}
                  </Button>
                )}
                <Button
                  id="next-step-button"
                  data-testid="next-step-button"
                  className={clsx("highlight", isRtl ? "is-rtl" : "")}
                  variant="contained"
                  disabled={!hasNextStep}
                  sx={navButton}
                  endIcon={nextStepIcon}
                  onClick={goToNextStep}
                >
                  {t("Next Step")}
                </Button>
              </Stack>
            </Stack>
            {isPlaying && (
              <CoolDownProgress
                sx={cooldownProgress}
                duration={stepAutoplayConfig.delay}
                repeat={true}
                reverse={true}
              />
            )}
          </Paper>
        </Box>
      </Slide>
    </>
  )
}
